import { IconCode } from "@multiweb/types/contracts";

/** Tabulka znaků a jejich velikosti */
const FONT_TABLE: Map<string, {w: number; h: number}> = new Map;
/** Tabulka znaků pro ikony */
const ICON_TABLE: Map<IconCode, number> = new Map;
/** Výchozí velikost fontu */
const DEFAULT_FONT_SIZE = 13;
/** Výchozí velikost odsazení */
const DEFUALT_PADDING = 8;

let fontSize: number = NaN;
let paddingLeft: number = NaN;
let paddingRight: number = NaN;

/**
 * Funkce projde předaný textový řetězec znak po znaku, zkontroluje velikost daneho znaku a uloží ji do tabulky.
 *
 * Načte si pomocí getTarget testovací element pro .toolbar-btn-test, který ma speciální styl pro měření fontu a to takový,
 * že krom nastavené korektní velikosti fontu nemá nic, aby bylo možné změřit úplně přesně šířku daného znaku.
 *
 * Samotné měření probíha tak, že se prochází text po jednotlivých znacích každý znak se vloži samostatně do těla elementu
 * a provede se měření a uložení do tabulky znaků.
 *
 * @param text Text k detekci
 */
function updateFontTable(text: string): void {
    const target = getTarget("character-size-test-element");

    fontSize = getProperty("fontSize");
    target.style.fontSize = `${fontSize}px`;

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (FONT_TABLE.has(ch)) continue;

        target.innerHTML = ch === " " ? "&nbsp;" : ch;
        const rect = target.getBoundingClientRect();
        const obj = {
            w: rect.width,
            h: rect.height,
        };
        FONT_TABLE.set(ch, obj);
    }
}

/**
 * Pomocná funkce, která vrací target pro detekční funkce na základě CSS třídy.
 *
 * Vytvoří testovací element s třidou 'size-tester-element' do body,
 * který ma specifické stylování je absolutně pozicovany, neviditelný a zmenšený na 0 pixelů.
 * A do tohoto testovacího elementu najde/vytvoří div s předanou třídou a vratí jej.
 *
 * @param className Název CSS třídy
 */
function getTarget(className: string): HTMLElement {
    const rootTarget = getOrCreate("size-tester-element");
    return getOrCreate(className, rootTarget);
}

/**
 * Funkce vyhledá nebo vytvoří element s třidou, ktera je v className. Pokud je vytvořen nový je připojen do DOM. Místo je určeno buď scope a nebo je přídán do document.body.
 *
 * @param className Název hledané/vytvářené třídy
 * @param scope Pokud je vyplněny tak se při prohledávání dané třídy omezí na daný scope. A zároveň pokud je vytvářen nový je přidán do tohoto scope.
 */
function getOrCreate(className: string, scope?: HTMLElement): HTMLElement {
    const innerScope = scope ?? document;
    let element: HTMLElement | null = innerScope.querySelector(`.${className}`);

    if (element == null) {
        const appendScope = scope ?? document.body;

        element = document.createElement("div");
        element.className = className;
        element.setAttribute("aria-hidden", "true");

        appendScope.appendChild(element);
    }

    return element;
}

/**
 * Zjistí šířku odsazení
 *
 * Načte si pomocí getTarget testovací element pro .test-padding, která ma nastavené stejné styly jako toolbar,
 * přečte pomocí getComputedStyle předanou vlastnost a vrátí její hodnotu čí undefined.
 *
 * @param property
 */
function findPropertyValue(property: "paddingLeft" | "paddingRight" | "fontSize"): number | undefined {
    const target = getTarget("test-padding");
    const result: number = parseInt(getComputedStyle(target)[property]);

    if (isNaN(result)) {
        return undefined;
    }

    return result;
}

/**
 * Vrátí šířku odsazení pro předanou stranu
 * @param property
 */
function getProperty(property: "paddingLeft" | "paddingRight" | "fontSize"): number {
    if (property === "fontSize") {
        if (isNaN(fontSize)) fontSize = findPropertyValue(property) ?? DEFAULT_FONT_SIZE;
    }
    if (property === "paddingLeft") {
        if (isNaN(paddingLeft)) paddingLeft = findPropertyValue(property) ?? DEFUALT_PADDING;

        return paddingLeft;
    } else {
        if (isNaN(paddingRight)) paddingRight = findPropertyValue(property) ?? DEFUALT_PADDING;

        return paddingRight;
    }
}

/**
 * Zjistí velikost šířku textu na základě předaného stringu
 * @param text
 */
function detectTextSize(text: string): number {
    let w = 0;

    updateFontTable(text);

    for (let i = 0; i < text.length; i++) {
        w += (FONT_TABLE.get(text[i])?.w ?? 0);
    }

    return w;
}

/**
 * Vrátí šířku tlačítka toolbaru bez ikon na základě předaného řetězce. Používa se proto, že je třeba znát šířku ještě před vykreslením.
 * @param text
 */
export function getToolbarBtnWidth(text: string): number {
    return getProperty("paddingLeft") + getProperty("paddingRight") + detectTextSize(text);
}

/**
 * Vrátí šířku dané ikony. Používa se proto, že je třeba znát šířku ještě před vykreslením.
 *
 * Načte testovací element pro ikonu .Icon, který ma nastavené obecné styly pro ikony, vloži předanou ikonu
 * a pomoci getBoundingClientRect načte velikost elentu dané ikony.
 *
 * @param icon Použitá ikona
 * @param hasPaddingLeft Zda se má k výpočtu přičíst i levé odsazení
 * @param hasPaddingRight Zda se má k výpočtu přičíst i pravé odsazení
 */
export function getIconWidth(icon: IconCode, hasPaddingLeft = true, hasPaddingRight = true): number {
    let iconWidth: number | undefined = ICON_TABLE.get(icon);

    if (!iconWidth) {
        const target = getTarget("Icon");

        target.className = `Icon ${icon}`;
        const rect = target.getBoundingClientRect();
        iconWidth = rect.width;
        ICON_TABLE.set(icon, iconWidth);
    }

    return (hasPaddingLeft ? getProperty("paddingLeft") : 0) + (hasPaddingRight ? getProperty("paddingRight") : 0) + iconWidth;
}
